// 고객 입력 요청 링크 발급 버튼 (CRM 헤더 + 신청서 디테일 공용).
//
// 동작: 버튼을 누르면 별도 입력 없이 즉시 토큰을 발급하고, 링크 URL 을 클립보드에
// 자동복사한 뒤 짧은 토스트로 "복사됐으니 고객에게 보내세요" 를 안내한다(2.5초 후 사라짐).
//
// 두 가지 모드:
//   - 헤더(applicationFormId 없음): 빈 이름/번호로 새 고객 카드 + 신청서 + 토큰을 선생성.
//     고객이 폼을 제출하면 그때 이름·전화번호가 카드에 채워진다.
//   - 디테일(applicationFormId 지정): 그 신청서에 새 토큰을 묶어 재발급한다. 새 카드를
//     만들지 않고 기존 활성 링크는 무효화 — "입력 링크 다시 복사" 용도.
//
// 링크 유효기간은 1시간. 고객이 입력하지 않으면 토큰이 만료돼 자동 폐기된다(이후 410).
// 발급된 평문 URL 은 응답에 한 번만 실려 오므로(DB 는 SHA-256 해시만 저장), 이 복사
// 시점이 평문 URL 을 쥘 수 있는 유일한 순간이다 — task #134 §보안 고려.

import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { issuePublicFormToken } from "../../api/crm";
import { crmKeys } from "../../hooks/crm";
import { useToast } from "../common/Toast";

import { ApiError, friendlyErrorMessage } from "../../api/_error";
// 링크 유효기간(시간). 고객 미입력 시 만료로 자동 폐기.
const EXPIRES_IN_HOURS = 1;

// 헤더 기본 버튼 스타일(채움형). 디테일은 className 으로 외곽선형을 넘긴다.
const DEFAULT_CLASS =
  "h-9 px-4 rounded-lg bg-brand-500 text-white text-xs font-semibold hover:bg-brand-600 transition flex items-center gap-1.5 disabled:opacity-60";

export default function FormLinkButton({
  applicationFormId,
  label = "🔗 폼링크 생성하기",
  className = DEFAULT_CLASS,
  disabled = false,
  title = "고객에게 입력 링크 발급 (클릭 즉시 복사)",
  openInNewTab = false,
  expiresInHours = EXPIRES_IN_HOURS,
  kind = "single",
}: {
  applicationFormId?: number;
  label?: string;
  className?: string;
  disabled?: boolean;
  title?: string;
  // true 면 클립보드 복사 대신 발급된 입력 페이지를 새 탭에서 바로 연다.
  openInNewTab?: boolean;
  // 링크 유효기간(시간). "이어서 작성 링크"는 하나를 길게 공유하므로 길게(예: 7일) 준다.
  expiresInHours?: number;
  // store 는 시간 제한 없이 로그인된 같은 판매점 직원만 여는 내부 입력 링크다.
  kind?: "single" | "store";
} = {}) {
  // 토스트 안내용 사람이 읽는 유효기간 라벨.
  const validLabel =
    kind === "store"
      ? "시간 제한 없음"
      : expiresInHours >= 24
      ? `${Math.round(expiresInHours / 24)}일 유효`
      : `${expiresInHours}시간 유효`;
  const qc = useQueryClient();
  const toast = useToast();
  // 발급은 네트워크 왕복 — 진행 중 재클릭을 막아 카드·토큰 중복 생성을 방지.
  const [issuing, setIssuing] = useState(false);

  const onClick = async () => {
    if (issuing) return;
    setIssuing(true);
    // 새 탭 모드: 토큰 발급은 서버 왕복(비동기)이라, 발급이 끝난 뒤 window.open 을
    // 부르면 사용자 클릭과의 연결이 끊겨 팝업 차단에 걸린다. 그래서 클릭 직후
    // 빈 탭을 먼저 열어두고, 토큰이 나오면 그 탭의 주소만 바꿔치기한다.
    const newTab = openInNewTab ? window.open("", "_blank") : null;
    try {
      // applicationFormId 가 있으면 그 신청서에 재발급, 없으면 새 빈 카드를 만든다.
      const res = await issuePublicFormToken({
        expiresInHours,
        kind,
        ...(applicationFormId != null ? { applicationFormId } : {}),
      });
      const url = `${window.location.origin}/form/${res.token}`;
      if (openInNewTab) {
        if (newTab) {
          newTab.location.href = url;
          toast.success(`입력 페이지를 새 탭에서 열었습니다 (${validLabel})`);
        } else {
          // 팝업이 차단돼 미리 연 탭이 없으면, 클립보드 복사로 폴백한다.
          try {
            await navigator.clipboard.writeText(url);
            toast.success(`팝업이 차단돼 링크를 복사했습니다. 새 탭에 붙여넣으세요 (${validLabel})`);
          } catch {
            toast.error(`팝업 차단 — 링크를 직접 복사하세요: ${url}`);
          }
        }
      } else {
        try {
          await navigator.clipboard.writeText(url);
          toast.success(`링크가 복사되었습니다. 고객에게 보내세요 (${validLabel})`);
        } catch {
          // 클립보드 권한 등으로 자동복사 실패 — 토큰은 이미 발급됨. 수동 복사 안내.
          toast.error(`복사 실패 — 링크를 직접 복사하세요: ${url}`);
        }
      }
      // 발급이 바꾸는 데이터군만 새로고침한다(옛 ["crm"] 광역 대신).
      // 헤더 모드는 새 고객 카드+신청서+토큰을 만들고, 디테일 모드는 그 신청서에 토큰을
      // 재발급한다 → 고객/신청서 목록·집계, 그리고 단건 신청서(있을 때). 부가서비스·마스터·
      // 카탈로그·서식 템플릿 등은 링크 발급이 건드리지 않으므로 무효화 대상에서 뺀다.
      qc.invalidateQueries({ queryKey: ["crm", "customers"] });
      qc.invalidateQueries({ queryKey: ["crm", "applications"] });
      if (applicationFormId != null) {
        qc.invalidateQueries({ queryKey: crmKeys.application(applicationFormId) });
      }
      qc.invalidateQueries({ queryKey: crmKeys.stageCounts() });
      qc.invalidateQueries({ queryKey: crmKeys.pipelineCounts() });
    } catch (err) {
      // 발급 실패 시 미리 열어둔 빈 탭은 닫아 사용자에게 빈 창을 남기지 않는다.
      if (newTab) newTab.close();
      const msg = friendlyErrorMessage(err);
      // 409 INTAKE_LOCKED — 이미 데이터가 입력된(데이터완료 이상) 카드라 재발급 불가.
      // ⚠️ 상태는 message 가 아니라 ApiError.status·serverMessage 에 있다 — 옛 `/HTTP 409/.test(msg)` 는
      // _error.ts 가 문구를 사람 말로 바꾼 뒤로 늘 false 라, 이 안내 대신 일반 오류가 떴다.
      if (
        (err instanceof ApiError && (err.status === 409 || /INTAKE_LOCKED/.test(err.serverMessage))) ||
        /INTAKE_LOCKED|HTTP 409/.test(msg)
      ) {
        toast.error(
          "이미 데이터가 입력된 카드입니다. 디테일에서 직접 수정해 주세요.",
        );
      } else {
        toast.error(`링크 발급 실패: ${msg}`);
      }
    } finally {
      setIssuing(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={onClick}
        disabled={disabled || issuing}
        className={className}
        title={title}
      >
        {issuing ? "발급 중…" : label}
      </button>
    </>
  );
}
